<script setup lang="ts">
import { computed, type HTMLAttributes } from "vue";
import { cn } from "@/lib/utils";

type ButtonVariant = "primary" | "secondary" | "ghost" | "surface" | "control" | "tab" | "menu";
type ButtonSize = "small" | "default" | "icon" | "icon-small" | "icon-compact";
type ButtonRadius = "small" | "control" | "pill";

interface Props {
  variant?: ButtonVariant;
  size?: ButtonSize;
  radius?: ButtonRadius;
  type?: "button" | "submit" | "reset";
  class?: HTMLAttributes["class"];
}

const props = withDefaults(defineProps<Props>(), {
  variant: "secondary",
  size: "default",
  type: "button",
});

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    "border-transparent bg-accent font-semibold text-accent-foreground",
  secondary:
    "border-app-border bg-app-material text-text-muted hover:bg-app-elevated hover:text-text-primary",
  ghost:
    "border-transparent bg-transparent text-text-muted hover:bg-app-elevated hover:text-text-primary",
  surface:
    "border-app-border bg-app-elevated text-text-primary hover:bg-app-selected aria-expanded:bg-app-selected",
  control:
    "border-transparent bg-transparent text-text-muted hover:bg-app-selected hover:text-text-primary enabled:active:scale-90 disabled:opacity-40",
  tab:
    "border-transparent bg-transparent text-text-muted hover:bg-app-elevated hover:text-text-primary [&.is-selected]:bg-accent [&.is-selected]:font-semibold [&.is-selected]:text-accent-foreground",
  menu:
    "border-transparent bg-transparent text-text-primary hover:bg-app-selected focus-visible:bg-app-selected aria-disabled:cursor-not-allowed aria-disabled:text-text-muted",
};

const sizeClasses: Record<ButtonSize, string> = {
  small: "min-h-10 px-3 py-2 text-xs",
  default: "min-h-11 px-4 py-2.5 text-[13px]",
  icon: "size-11 p-0",
  "icon-small": "size-9 p-0",
  "icon-compact": "size-8 p-0",
};

const defaultRadius: Record<ButtonSize, ButtonRadius> = {
  small: "small",
  default: "control",
  icon: "control",
  "icon-small": "small",
  "icon-compact": "small",
};

const radiusClasses: Record<ButtonRadius, string> = {
  small: "rounded-small",
  control: "rounded-control",
  pill: "rounded-full",
};

const motionVariants = new Set<ButtonVariant>(["primary", "secondary", "ghost"]);

const classes = computed(() =>
  cn(
    "app-button inline-flex cursor-pointer items-center justify-center gap-2 border transition-[color,background-color,border-color,transform,filter] focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-accent disabled:pointer-events-none disabled:cursor-default disabled:opacity-50",
    variantClasses[props.variant],
    sizeClasses[props.size],
    radiusClasses[props.radius ?? (props.variant === "tab" ? "pill" : defaultRadius[props.size])],
    props.class,
  ),
);
</script>

<template>
  <button
    :type="type"
    :class="classes"
    :data-button-motion="motionVariants.has(variant) ? '' : undefined"
  >
    <slot />
  </button>
</template>

<!-- Fine-pointer hover motion is shared here so touch controls keep their layout. -->
<style scoped>
@media (hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference) {
  .app-button[data-button-motion]:hover:not(:disabled) {
    transform: scale(1.025);
    filter: brightness(1.06);
  }

  .app-button[data-button-motion]:active:not(:disabled) {
    transform: scale(0.99);
    filter: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .app-button {
    transition: none;
  }
}
</style>
